import { CartCharge } from 'state/slices/shoppingCartSlice';

import {
  FxConversionRate,
  FxConversionRequest,
  FxConversionTax,
  TmPayerDetailsRequest,
  TmPaymentRequest,
  TmStudentDetailsRequest,
} from 'services/payment-intl';
import { calculateChargeTotal, getDueNowPayableCharges } from 'utils/charge-utils';

import {
  TRANSFERMATE_COUNTRY,
  TRANSFERMATE_PAN_PATTERN,
  TRANSFERMATE_PAYMENT_METHOD_NAMES,
  TRANSFERMATE_SOURCE_OF_FUNDS,
} from './constants';
import { TransferMateFxFields } from './types';

type TransferMatePaymentFields = Partial<TransferMateFxFields>;
type TransferMateFeeName = 'same currency fee' | 'international transfer fee';

export function getInternationalPaymentAmount(charges: CartCharge[], currencyCode: string): number {
  return calculateChargeTotal(getDueNowPayableCharges(charges), currencyCode).minTotal;
}

export function buildFxConversionRequest(
  fields: TransferMateFxFields,
  paymentAmount: number,
): FxConversionRequest | null {
  const countryCode = String(fields.countryCode ?? '')
    .trim()
    .toUpperCase();
  if (!countryCode || paymentAmount <= 0) return null;

  if (countryCode !== TRANSFERMATE_COUNTRY.INDIA) {
    return { countryCode, amount: String(paymentAmount) };
  }

  const indiaSourceOfFund = String(fields.indiaSourceOfFund ?? '').trim();
  const indiaDeclarationAmt = String(fields.indiaDeclarationAmt ?? '').trim();
  const payerDocument = String(fields.payerDocument ?? '')
    .trim()
    .toUpperCase();
  const hasValidSource = TRANSFERMATE_SOURCE_OF_FUNDS.some(
    ({ value }) => value === indiaSourceOfFund,
  );

  if (
    !hasValidSource ||
    Number(indiaDeclarationAmt) <= 0 ||
    !TRANSFERMATE_PAN_PATTERN.test(payerDocument)
  ) {
    return null;
  }

  return {
    countryCode,
    amount: String(paymentAmount),
    indiaSourceOfFund,
    indiaDeclarationAmt,
    payerDocument,
  };
}

export function getPaymentMethodName(rate: FxConversionRate): string {
  if (rate.type?.trim()) return rate.type.trim();

  return (
    TRANSFERMATE_PAYMENT_METHOD_NAMES[Number(rate.payment_method_id ?? rate.paymentMethodId)] ??
    `Payment Method ${rate.payment_method_id ?? rate.paymentMethodId ?? ''}`
  );
}

/** Normalize Resident Payments' camelCase response while accepting legacy snake_case rates. */
export function normalizeTransferMateRate(rate: FxConversionRate): FxConversionRate {
  const rawRate = rate as FxConversionRate & Record<string, unknown>;
  return {
    ...rate,
    payment_method_id: rawRate.payment_method_id ?? rawRate.paymentMethodId ?? '',
    source_amount: rawRate.source_amount ?? rawRate.sourceAmount,
    source_currency: rawRate.source_currency ?? rawRate.sourceCurrency,
    converted_amount: rawRate.converted_amount ?? rawRate.convertedAmount,
    converted_currency: rawRate.converted_currency ?? rawRate.convertedCurrency,
    fx_rate: rawRate.fx_rate ?? rawRate.fxRate,
  };
}

function getPhoneNumber(locale?: string, number?: string): string {
  const countryCode = locale?.trim().replace(/^\+/, '') ?? '';
  const phoneNumber = number?.trim() ?? '';
  return countryCode || phoneNumber ? `+${countryCode}${phoneNumber}` : '';
}

export function getTransferMateFees(taxes: readonly FxConversionTax[] = []): FxConversionTax[] {
  return taxes.filter((tax) => {
    const name = tax.name?.trim().toLowerCase();
    return name === 'same currency fee' || name === 'international transfer fee';
  });
}

export function getTransferMateFee(
  feeName: TransferMateFeeName,
  taxes: readonly FxConversionTax[] = [],
): FxConversionTax | undefined {
  return taxes.find((tax) => tax.name?.trim().toLowerCase() === feeName);
}

export function getTransferMateFeeLabel(tax: FxConversionTax): string {
  const name = tax.name?.trim().toLowerCase();
  const label = name === 'same currency fee' ? 'Same Currency Fee' : 'International Transfer Fee';
  return tax.percentage ? `${label} (${tax.percentage}%)` : label;
}

function buildStudentDetails(fields: TransferMatePaymentFields): TmStudentDetailsRequest {
  return {
    name: fields.studentName || '',
    country: fields.studentCountryCode || '',
    city: fields.studentCity || '',
    address: fields.studentAddress || '',
    postalCode: fields.studentPostalCode || '',
    phoneNumber: getPhoneNumber(fields.studentPhoneLocale, fields.studentPhoneNumber),
    dob: fields.studentDob || '',
    ...(fields.studentNumber?.trim() && { number: fields.studentNumber.trim() }),
    email: fields.studentEmail || '',
    ...(fields.studentChineseName && { chineseName: fields.studentChineseName }),
    ...(fields.studentDocument && { document: fields.studentDocument }),
  };
}

function buildPayerDetails(
  fields: TransferMatePaymentFields,
  studentDetails: TmStudentDetailsRequest,
): TmPayerDetailsRequest {
  if ((fields.whoIsMakingPayment || 'student') === 'student') {
    return {
      name: studentDetails.name,
      nationality: studentDetails.country,
      city: studentDetails.city,
      address: studentDetails.address,
      postalCode: studentDetails.postalCode,
      phoneNumber: studentDetails.phoneNumber,
      email: studentDetails.email,
      relationshipToStudent: fields.relationshipToStudent,
      ...((fields.payerChineseName || fields.studentChineseName) && {
        chineseName: fields.payerChineseName || fields.studentChineseName,
      }),
      ...((fields.studentPayerDocument || fields.studentDocument || fields.payerDocument) && {
        document: fields.studentPayerDocument || fields.studentDocument || fields.payerDocument,
      }),
    };
  }

  return {
    name: fields.payerName || '',
    nationality: fields.payerCountryCode || '',
    city: fields.payerCity || '',
    address: fields.payerAddress || '',
    postalCode: fields.payerPostalCode || '',
    phoneNumber: getPhoneNumber(fields.payerPhoneLocale, fields.payerPhoneNumber),
    email: fields.payerEmail || '',
    relationshipToStudent: fields.relationshipToStudent,
    ...(fields.payerChineseName && { chineseName: fields.payerChineseName }),
    ...((fields.payerCountryDocument || fields.payerDocument) && {
      document: fields.payerCountryDocument || fields.payerDocument,
    }),
  };
}

function buildIndiaPaymentDetails(fields: TransferMatePaymentFields) {
  return {
    ...(fields.indiaFeeExceed !== '' &&
      fields.indiaFeeExceed !== undefined && {
        indiaFeeExceed: Number(fields.indiaFeeExceed),
      }),
    ...(fields.indiaSourceOfFund && { indiaSourceOfFund: fields.indiaSourceOfFund }),
    ...(fields.indiaDeclarationAmt && {
      indiaDeclarationAmt: Number(fields.indiaDeclarationAmt),
    }),
  };
}

export function buildTransferMatePaymentRequest(
  fields: TransferMatePaymentFields,
  rate: FxConversionRate,
  paymentAmount: number,
  websiteHost: string,
  termsAccepted: boolean,
): TmPaymentRequest {
  const whoIsMakingPayment = fields.whoIsMakingPayment || 'student';
  const studentDetails = buildStudentDetails(fields);
  const payerDetails = buildPayerDetails(fields, studentDetails);

  return {
    paymentAmount,
    paymentTypeId: 20,
    paymentMethodId: Number(rate.payment_method_id ?? rate.paymentMethodId),
    paymentMemo: 'Application fee',
    websiteHost,
    countryCode: String(fields.countryCode || '')
      .trim()
      .toUpperCase(),
    ...(fields.stateCode && { stateCode: fields.stateCode }),
    paymentPurpose: fields.paymentPurpose || '',
    whoIsMakingPayment,
    eduInstituteName: fields.educationInstituteName || '',
    agreesToTerms: termsAccepted,
    agreesToFees: termsAccepted,
    agreesToDuplicate: true,
    ...buildIndiaPaymentDetails(fields),
    studentDetails,
    payerDetails,
  };
}

/**
 * TransferMate Card must only show card methods. The FX endpoint can return
 * bank-transfer/BACS entries as well, so filter those defensively before they
 * reach the payment-method selector.
 */
export function filterTransferMateCardRates(
  rates: readonly FxConversionRate[],
): FxConversionRate[] {
  return rates.filter((rate) => {
    const methodName = getPaymentMethodName(rate).toLowerCase();
    return !/(?:bank\s*transfer|bacs|direct\s*debit|wire\s*transfer)/i.test(methodName);
  });
}

export function formatCurrencyAmount(currency?: string, amount?: string | number): string {
  if (!currency || amount === undefined || amount === null || Number.isNaN(Number(amount)))
    return '';

  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
